import {
  Avatar,
  AvatarProps,
  Divider,
  Group,
  Menu,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core"
import { IconMail, IconUser } from "@tabler/icons-react"
import { useMemo } from "react"

export type MenuItem = {
  label: string
  icon: React.ReactNode
  onClick: () => void
  isActive: boolean
}

type User = {
  organization: {
    name: string
  }
  email: string
  name: string
}

type Props = {
  user: User
  withAvatar?: boolean
  withName?: boolean
  color?: string
  avatarProps?: AvatarProps
  src?: string | null
  menuItems?: MenuItem[]
  dropdownPosition?: "left" | "right"
}

export const UserMenu = ({
  user,
  withAvatar = true,
  withName = true,
  avatarProps,
  src,
  menuItems,
  dropdownPosition,
}: Props) => {
  const initials = useMemo(
    () =>
      user?.name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase(),
    [user?.name]
  )

  return (
    <Menu zIndex={1000} position={dropdownPosition}>
      <Menu.Target>
        <UnstyledButton>
          <Group wrap="nowrap">
            {withName && (
              <Stack gap={0}>
                <Text size="sm" fw={700} c="var(--mantine-color-text)">
                  {user.organization?.name}
                </Text>
                <Text c="var(--mantine-color-text)">{user.email}</Text>
              </Stack>
            )}
            {withAvatar ? (
              src ? (
                <Avatar src={src} radius="xl" {...avatarProps} />
              ) : (
                <Avatar radius="xl" {...avatarProps}>
                  {initials}
                </Avatar>
              )
            ) : null}
          </Group>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Stack gap="xs">
          {user && (
            <Stack gap={2}>
              <Stack gap={2} px="sm" py="xs">
                <Group align="center" gap="xs">
                  <IconUser size={16} color="var(--tag-color-info)" />
                  <Text fw={600}>{user.organization?.name}</Text>
                </Group>
                <Group align="center" gap="xs">
                  <IconMail size={16} color="var(--tag-color-info)" />
                  <Text>{user.email}</Text>
                </Group>
              </Stack>
              <Divider w="100%" />
            </Stack>
          )}
          {menuItems &&
            menuItems.map((item, index) => (
              <Menu.Item
                key={index}
                leftSection={item.icon}
                onClick={item.onClick}
                className={item.isActive ? "active" : ""}
              >
                <Text>{item.label}</Text>
              </Menu.Item>
            ))}
        </Stack>
      </Menu.Dropdown>
    </Menu>
  )
}
